import { useState } from 'react';
import type { DashboardAccount } from '../../../types/admin-dashboard';
import AccountRowActions from './AccountRowActions';

const roleLabels = { SUPER_MASTER: 'Super Master', MASTER: 'Master', DISTRIBUTOR: 'Distributor', USER: 'User' } as const;
const roleTones = { SUPER_MASTER: 'account-role-super-master', MASTER: 'account-role-master', DISTRIBUTOR: 'account-role-distributor', USER: 'account-role-user' } as const;

const money = (value: string | number | null | undefined) => {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? number.toLocaleString('en-US', { maximumFractionDigits: 0 }) : '0';
};

const downlineText = (value: string | number | null | undefined) => {
  const number = Number(value ?? 0);
  if (!Number.isFinite(number) || number === 0) return '0';
  return `${number > 0 ? '+' : '-'}${money(Math.abs(number))}`;
};

const uplineTone = (value: string | number | null | undefined) => {
  const number = Number(value ?? 0);
  if (!Number.isFinite(number) || number === 0) return 'admin-upline-neutral';
  return number < 0 ? 'admin-upline-negative' : 'admin-upline-positive';
};

const totalUplineTone = (displayedValue: string) => {
  if (displayedValue.startsWith('-')) return 'admin-upline-negative';
  if (displayedValue.startsWith('+')) return 'admin-upline-positive';
  return 'admin-upline-neutral';
};

function visiblePages(page: number, totalPages: number): Array<number | 'ellipsis'> {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
  const items: Array<number | 'ellipsis'> = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(totalPages - 1, page + 1);
  if (start > 2) items.push('ellipsis');
  for (let current = start; current <= end; current += 1) items.push(current);
  if (end < totalPages - 1) items.push('ellipsis');
  items.push(totalPages);
  return items;
}

type Props = {
  accounts: DashboardAccount[];
  directUplineTotal: number;
  onLoginAs: (accountId: string) => Promise<void>;
  page: number;
  totalPages: number;
  total: number;
  onPageChange: (page: number) => void;
};

export default function AdminAccountsTable({ accounts, directUplineTotal, onLoginAs, page, totalPages, total, onPageChange }: Props) {
  const totalText = directUplineTotal === 0 ? '0' : `${directUplineTotal > 0 ? '-' : '+'}${money(Math.abs(directUplineTotal))}`;
  const pages = visiblePages(page, totalPages);
  const [actionSignals, setActionSignals] = useState<Record<string, number>>({});
  const openActions = (accountId: string) => setActionSignals((current) => ({ ...current, [accountId]: (current[accountId] || 0) + 1 }));

  return (
    <section className="overflow-hidden rounded-2xl border border-white/10 bg-[rgba(18,26,43,0.92)] shadow-[0_12px_26px_rgba(0,0,0,0.24)]">
      <div className="max-w-full overflow-x-auto [scrollbar-width:thin] [scrollbar-color:rgba(125,211,252,0.30)_transparent] [-webkit-overflow-scrolling:touch]">
        <table className="w-max min-w-full table-auto border-collapse text-[11px] sm:text-sm">
          <thead className="bg-[#111827] text-left text-[9px] font-black uppercase tracking-[0.10em] text-slate-200 shadow-[0_1px_0_rgba(255,255,255,0.06)] sm:text-xs">
            <tr>
              <th className="min-w-[8.5rem] border-b border-white/[0.06] px-2 py-2.5 sm:px-3">Username</th>
              <th className="min-w-[7.5rem] border-b border-white/[0.06] px-1.5 py-2.5 sm:px-3">Name</th>
              <th className="min-w-[6.5rem] whitespace-nowrap border-b border-white/[0.06] px-2 py-2.5 text-right sm:px-3">
                Upline <span className={`numeric-data block text-[8px] font-black sm:inline sm:text-[10px] ${totalUplineTone(totalText)}`}>{totalText}</span>
              </th>
              <th className="min-w-[7.5rem] whitespace-nowrap border-b border-white/[0.06] px-2 py-2.5 text-right text-white sm:px-3">Balance</th>
              <th className="min-w-[6.5rem] whitespace-nowrap border-b border-white/[0.06] px-2 py-2.5 text-center text-white sm:px-3">Role</th>
            </tr>
          </thead>
          <tbody>
            {accounts.length ? accounts.map((account) => (
              <tr key={account.id} className="border-b border-white/[0.06] bg-transparent last:border-b-0 hover:bg-white/[0.025]">
                <td className="px-2 py-2.5 align-middle sm:px-3">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <button type="button" onClick={() => openActions(account.id)} className="admin-data-primary min-w-0 flex-1 break-words text-left text-[13px] font-normal leading-tight outline-none [-webkit-tap-highlight-color:transparent] sm:text-[13px]" aria-label={`Open actions for ${account.username}`}>{account.username}</button>
                    <AccountRowActions accountId={account.id} username={account.username} role={account.role} canLoginAs={account.isActive} onLoginAs={onLoginAs} openSignal={actionSignals[account.id] || 0} />
                  </div>
                </td>
                <td className="px-1.5 py-2.5 align-middle sm:px-3">
                  <button type="button" onClick={() => openActions(account.id)} className="admin-data-primary flex min-w-0 items-start gap-1.5 text-left text-[13px] font-normal leading-tight outline-none [-webkit-tap-highlight-color:transparent] sm:text-[13px]" aria-label={`Open actions for ${account.displayName || account.username}`}>
                    <span className={`mt-0.5 h-2 w-2 shrink-0 rounded-full ${account.isOnline ? 'bg-emerald-400' : 'bg-slate-600'}`} aria-label={account.isOnline ? 'Online' : 'Offline'} />
                    <span className="min-w-0 break-words">{account.displayName || '—'}</span>
                  </button>
                </td>
                <td className={`numeric-data whitespace-nowrap px-2 py-2.5 text-right align-middle font-black tracking-tight sm:px-3 ${uplineTone(account.wallet?.cashUpline)}`}>
                  {downlineText(account.wallet?.cashUpline)}
                </td>
                <td className="admin-balance-text numeric-data whitespace-nowrap px-2 py-2.5 text-right align-middle text-[11px] font-black tracking-tight sm:px-3 sm:text-[12px]">
                  {money(account.wallet?.availableBalance)}
                </td>
                <td className="whitespace-nowrap px-2 py-2.5 text-center align-middle sm:px-3">
                  <span className={`account-role-label text-[10px] font-black uppercase tracking-[0.04em] ${roleTones[account.role]}`}>{roleLabels[account.role]}</span>
                </td>
              </tr>
            )) : (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-sm font-semibold text-slate-400">No accounts match the selected filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="admin-border flex flex-wrap items-center justify-between gap-2 border-t px-3 py-3">
        <p className="admin-text-muted text-[10px] font-semibold">{total.toLocaleString('en-US')} account{total === 1 ? '' : 's'} · 25 per page</p>
        <nav className="flex items-center gap-1" aria-label="Account pages">
          <button type="button" disabled={page <= 1} onClick={() => onPageChange(page - 1)} className="crystal-btn crystal-smoky min-w-8 rounded-lg px-2 py-1.5 text-[10px] font-black disabled:opacity-35" aria-label="Previous page">‹</button>
          {pages.map((item, index) => item === 'ellipsis'
            ? <span key={`ellipsis-${index}`} className="admin-text-muted px-1 text-xs">…</span>
            : <button key={item} type="button" onClick={() => onPageChange(item)} className={`crystal-btn min-w-8 rounded-lg px-2 py-1.5 text-[10px] font-black ${item === page ? 'crystal-sapphire' : 'crystal-smoky'}`} aria-current={item === page ? 'page' : undefined}>{item}</button>)}
          <button type="button" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} className="crystal-btn crystal-smoky min-w-8 rounded-lg px-2 py-1.5 text-[10px] font-black disabled:opacity-35" aria-label="Next page">›</button>
        </nav>
      </div>
    </section>
  );
}
